import { EventHandler, Browser, isNullOrUndefined, detach } from '@syncfusion/ej2-base';
import * as events from '../base/constant';
import * as classes from '../base/classes';
/**
 * `Resize` module is used to resize the editor
 */
export class Resize {
    constructor(parent) {
        this.parent = parent;
        this.addEventListener();
        this.isDestroyed = false;
        this.isResizing = false;
        this.iframeMouseUpBoundFn = this.iframeMouseUp.bind(this);
    }
    addEventListener() {
        if (this.parent.isDestroyed) {
            return;
        }
        this.parent.on(events.initialEnd, this.renderResizable, this);
        this.parent.on(events.modelChanged, this.onPropertyChanged, this);
        this.parent.on(events.destroy, this.destroy, this);
    }
    onPropertyChanged(e) {
        for (const prop of Object.keys(e.newProp)) {
            if (prop === 'enableResize') {
                if (!e.newProp.enableResize) {
                    this.destroy();
                }
                else {
                    this.renderResizable();
                }
            }
        }
    }
    renderResizable() {
        const enableRtlClass = (this.parent.enableRtl) ? classes.CLS_RTE_RES_WEST : classes.CLS_RTE_RES_EAST;
        this.resizer = this.parent.createElement('div', {
            id: this.parent.getID() + '-resizable', className: 'e-icons'
                + ' ' + classes.CLS_RTE_RES_HANDLE + ' ' + enableRtlClass
        });
        this.parent.element.classList.add(classes.CLS_RTE_RES_CNT);
        this.parent.rootContainer.appendChild(this.resizer);
        this.parent.rootContainer.classList.add('e-resize-enabled');
        if (this.parent.iframeSettings.enable) {
            this.parent.inputElement.classList.add('e-resize-enabled');
            this.parent.contentModule.getDocument().addEventListener('mouseup', this.iframeMouseUpBoundFn);
        }
        this.iframeElement = this.parent.contentModule.getDocument().querySelectorAll('iframe');
        if (!isNullOrUndefined(this.iframeElement)) {
            this.iframeElement.forEach((iframe) => {
                EventHandler.add(iframe, 'load', this.onIFrameLoad, this);
            });
        }
        this.touchStartEvent = (Browser.info.name === 'msie') ? 'pointerdown' : 'touchstart';
        EventHandler.add(this.resizer, 'mousedown', this.resizeStart, this);
        EventHandler.add(this.resizer, this.touchStartEvent, this.resizeStart, this);
    }
    onIFrameLoad(e) {
        const iframe = e.target;
        if (iframe.nodeName === 'IFRAME' && iframe.contentDocument) {
            EventHandler.add(iframe.contentDocument, 'mouseup', this.stopResize, this);
        }
    }
    removeMouseUpEventListener(iframe) {
        if (iframe.contentDocument) {
            EventHandler.remove(iframe.contentDocument, 'mouseup', this.stopResize);
        }
    }
    resizeStart(e) {
        this.isResizing = false;
        if (e.cancelable) {
            e.preventDefault();
        }
        this.wireResizeEvents();
        this.parent.notify(events.resizeInitialized, {});
        const args = { event: e, requestType: 'editor' };
        this.parent.trigger(events.resizeStart, args, (resizeStartArgs) => {
            if (resizeStartArgs.cancel) {
                this.unwireResizeEvents();
            }
        });
    }
    performResize(e) {
        this.isResizing = true;
        const args = { event: e, requestType: 'editor' };
        this.parent.trigger(events.onResize, args, (resizingArgs) => {
            if (resizingArgs.cancel) {
                this.unwireResizeEvents();
            }
        });
        const boundRect = this.parent.element.getBoundingClientRect();
        if (this.isMouseEvent(e)) {
            this.parent.element.style.height = e.clientY - boundRect.top + 'px';
            this.parent.element.style.width = (!this.parent.enableRtl) ? e.clientX - boundRect.left + 'px' :
                boundRect.right - e.clientX + 'px';
            if (this.parent.toolbarModule) {
                const toolBarEle = this.parent.toolbarModule.getToolbarElement();
                if (!isNullOrUndefined(toolBarEle) && !isNullOrUndefined(toolBarEle.parentElement)) {
                    if (toolBarEle.parentElement.classList.contains(classes.CLS_TB_FLOAT) && this.parent.toolbarSettings.enableFloating &&
                        this.parent.getToolbar() && !this.parent.inlineMode.enable) {
                        const sourceViewPanel = this.parent.element.querySelector('#' + this.parent.getID() + '_source-view');
                        const defaultContentPanel = this.parent.contentModule.getPanel();
                        let isCodeViewEnabled = false;
                        if (sourceViewPanel && this.parent.element.querySelector('.e-source-code-enabled')) {
                            isCodeViewEnabled = true;
                        }
                        const targetPanelWidth = isCodeViewEnabled
                            ? sourceViewPanel.getBoundingClientRect().width
                            : defaultContentPanel.getBoundingClientRect().width;
                        toolBarEle.style.width = targetPanelWidth + 'px';
                    }
                }
            }
        }
        else {
            const eventType = Browser.info.name !== 'msie' ? e.touches[0] : e;
            this.parent.element.style.height = eventType.clientY - boundRect.top + 'px';
            this.parent.element.style.width = (!this.parent.enableRtl) ? eventType.clientX - boundRect.left + 'px' : boundRect.right - eventType.clientX + 'px';
        }
        const rteContent = this.parent.element.querySelector('#' + this.parent.getID() + '_source-view');
        if (!isNullOrUndefined(rteContent)) {
            rteContent.style.height = this.parent.element.style.height;
        }
        this.parent.refreshUI();
    }
    stopResize(e) {
        this.isResizing = false;
        this.parent.refreshUI();
        this.unwireResizeEvents();
        const args = { event: e, requestType: 'editor' };
        this.parent.trigger(events.resizeStop, args);
    }
    getEventType(e) {
        return (e.indexOf('mouse') > -1) ? 'mouse' : 'touch';
    }
    isMouseEvent(e) {
        let isMouse = false;
        if (this.getEventType(e.type) === 'mouse' || (!isNullOrUndefined(e.pointerType) &&
            this.getEventType(e.pointerType) === 'mouse')) {
            isMouse = true;
        }
        return isMouse;
    }
    wireResizeEvents() {
        EventHandler.add(document, 'mousemove', this.performResize, this);
        EventHandler.add(document, 'mouseup', this.stopResize, this);
        this.touchMoveEvent = (Browser.info.name === 'msie') ? 'pointermove' : 'touchmove';
        this.touchEndEvent = (Browser.info.name === 'msie') ? 'pointerup' : 'touchend';
        EventHandler.add(document, this.touchMoveEvent, this.performResize, this);
        EventHandler.add(document, this.touchEndEvent, this.stopResize, this);
    }
    unwireResizeEvents() {
        EventHandler.remove(document, 'mousemove', this.performResize);
        EventHandler.remove(document, 'mouseup', this.stopResize);
        EventHandler.remove(document, this.touchMoveEvent, this.performResize);
        EventHandler.remove(document, this.touchEndEvent, this.stopResize);
    }
    destroy() {
        if (this.isDestroyed) {
            return;
        }
        this.removeEventListener();
        if (this.resizer) {
            detach(this.resizer);
            this.resizer = null;
        }
        this.isDestroyed = true;
    }
    removeEventListener() {
        this.parent.off(events.initialEnd, this.renderResizable);
        this.parent.off(events.modelChanged, this.onPropertyChanged);
        this.parent.element.classList.remove(classes.CLS_RTE_RES_CNT);
        if (this.parent && this.parent.rootContainer && this.parent.rootContainer.classList.contains('e-resize-enabled')) {
            this.parent.rootContainer.classList.remove('e-resize-enabled');
        }
        if (this.parent.iframeSettings.enable && !isNullOrUndefined(this.parent.inputElement)) {
            this.parent.inputElement.classList.remove('e-resize-enabled');
            this.parent.contentModule.getDocument().removeEventListener('mouseup', this.iframeMouseUpBoundFn);
        }
        if (!isNullOrUndefined(this.iframeElement)) {
            this.iframeElement.forEach((iframe) => {
                this.removeMouseUpEventListener(iframe);
                EventHandler.remove(iframe, 'load', this.onIFrameLoad);
            });
        }
        if (this.resizer) {
            EventHandler.remove(this.resizer, 'mousedown', this.resizeStart);
            EventHandler.remove(this.resizer, this.touchStartEvent, this.resizeStart);
            detach(this.resizer);
        }
        this.parent.off(events.destroy, this.destroy);
        this.iframeMouseUpBoundFn = null;
    }
    iframeMouseUp(e) {
        if (this.isResizing) {
            this.stopResize(e);
        }
        else {
            return;
        }
    }
    /**
     * For internal use only - Get the module name.
     *
     * @returns {void}
     * @hidden
     */
    getModuleName() {
        return 'resize';
    }
}
